import {
  contentOf,
  facetsOf,
  isCosmetic,
  isStepKey,
  keyString,
  facetIn,
  settleSteps,
  unionKeys,
  valueOf,
  type Facet,
  type FacetKey,
  type Facets,
  type WorkflowContent,
  type WorkflowGraphShape,
  type WorkflowLinkLike,
  type WorkflowStepLike,
} from "./workflowFacets.ts";
import { CONDITION_OUTLETS, validateWorkflowGraph, workflowStepBranches } from "./workflowGraph.ts";

export type { WorkflowContent, WorkflowGraphShape, WorkflowLinkLike, WorkflowStepLike };

type StepOf<G extends WorkflowGraphShape> = G["nodes"][number];
type LinkOf<G extends WorkflowGraphShape> = G["edges"][number];

/** A step field a diff names: its kind, its label, or one top-level key of its config. */
export type StepField = "type" | "label" | `config.${string}`;

export interface FieldChange {
  readonly field: StepField;
  /** Undefined when the config key was absent. */
  readonly before: unknown;
  readonly after: unknown;
  /** Both sides as text when both are strings, for `wordDiff`. */
  readonly text: { readonly before: string; readonly after: string } | null;
}

export interface WorkflowContentDiff<G extends WorkflowGraphShape = WorkflowGraphShape> {
  /** New steps, in the after graph's order. */
  readonly added: ReadonlyArray<StepOf<G>>;
  /** Steps that went, as they were, in the before graph's order. */
  readonly removed: ReadonlyArray<StepOf<G>>;
  /** Steps whose kind, label or config changed, field by field, in the after graph's order. */
  readonly changed: ReadonlyArray<{ readonly id: string; readonly fields: ReadonlyArray<FieldChange> }>;
  /** Steps whose position changed by at least {@link MOVE_EPSILON} on either axis. */
  readonly moved: ReadonlyArray<{
    readonly id: string;
    readonly from: StepOf<G>["position"];
    readonly to: StepOf<G>["position"];
  }>;
  readonly links: {
    readonly added: ReadonlyArray<LinkOf<G>>;
    readonly removed: ReadonlyArray<LinkOf<G>>;
    /**
     * A link that now leaves the same outlet for another step: its end
     * travels. Removed and added links from the same step and outlet pair
     * up one to one in link id order. A guess at intent.
     */
    readonly rerouted: ReadonlyArray<{ readonly before: LinkOf<G>; readonly after: LinkOf<G> }>;
  };
  readonly name: { readonly before: string; readonly after: string } | null;
  readonly description: { readonly before: string | null; readonly after: string | null } | null;
  readonly layoutChanged: boolean;
  /** Nothing at all changed, order included. */
  readonly empty: boolean;
}

/** Below this many chart units on both axes, a position change is float noise, not a move. */
export const MOVE_EPSILON = 0.5;

/**
 * What changed from `before` to `after`. Total: a step kind or config key
 * added after this was written diffs with no change here.
 */
export function diffWorkflowContent<G extends WorkflowGraphShape>(
  before: WorkflowContent<G>,
  after: WorkflowContent<G>,
): WorkflowContentDiff<G> {
  const from = facetsOf(before);
  const to = facetsOf(after);
  const added: Array<StepOf<G>> = [];
  const changed: Array<{ readonly id: string; readonly fields: ReadonlyArray<FieldChange> }> = [];
  const moved: Array<{ readonly id: string; readonly from: StepOf<G>["position"]; readonly to: StepOf<G>["position"] }> = [];
  for (const node of after.graph.nodes) {
    const old = from.nodes.get(node.id);
    if (!old) {
      added.push(node);
      continue;
    }
    if (from.stepContent.get(node.id) !== to.stepContent.get(node.id)) {
      changed.push({ id: node.id, fields: fieldChanges(from, to, node.id) });
    }
    if (Math.abs(node.position.x - old.position.x) >= MOVE_EPSILON || Math.abs(node.position.y - old.position.y) >= MOVE_EPSILON) {
      moved.push({ id: node.id, from: old.position, to: node.position });
    }
  }
  const removed = before.graph.nodes.filter((node) => !to.nodes.has(node.id));
  const linksGone = before.graph.edges.filter((edge) => linkValue(from, edge.id) !== linkValue(to, edge.id));
  const linksNew = after.graph.edges.filter((edge) => linkValue(from, edge.id) !== linkValue(to, edge.id));
  const rerouted = pairReroutes(linksGone, linksNew);
  const paired = new Set(rerouted.flatMap((pair) => [pair.before, pair.after]));
  const name = before.name === after.name ? null : { before: before.name, after: after.name };
  const description = before.description === after.description ? null : { before: before.description, after: after.description };
  const layoutChanged = before.graph.layout !== after.graph.layout;
  return {
    added,
    removed,
    changed,
    moved,
    links: {
      added: linksNew.filter((edge) => !paired.has(edge)),
      removed: linksGone.filter((edge) => !paired.has(edge)),
      rerouted,
    },
    name,
    description,
    layoutChanged,
    empty: unionKeys(from, to).every((text) => !touches({ before: from, after: to }, text)),
  };
}

function fieldChanges(from: Facets, to: Facets, id: string): ReadonlyArray<FieldChange> {
  const keys = new Set([...(from.stepKeys.get(id) ?? []), ...(to.stepKeys.get(id) ?? [])]);
  const fields: FieldChange[] = [];
  for (const text of keys) {
    const was = from.byKey.get(text);
    const now = to.byKey.get(text);
    const key = (was ?? now)?.key;
    if (!key || key.kind === "position" || valueOf(was) === valueOf(now)) continue;
    const field = stepField(key);
    if (field === null) continue;
    const strings = typeof was?.raw === "string" && typeof now?.raw === "string" ? { before: was.raw, after: now.raw } : null;
    fields.push({ field, before: was?.raw, after: now?.raw, text: strings });
  }
  return fields.sort((left, right) => compareText(fieldRank(left.field), fieldRank(right.field)));
}

function pairReroutes<E extends WorkflowLinkLike>(
  gone: ReadonlyArray<E>,
  added: ReadonlyArray<E>,
): ReadonlyArray<{ readonly before: E; readonly after: E }> {
  const byId = (left: E, right: E) => compareText(left.id, right.id);
  const waiting = new Map<string, E[]>();
  for (const edge of [...added].sort(byId)) waiting.set(outletOf(edge), [...(waiting.get(outletOf(edge)) ?? []), edge]);
  const pairs: Array<{ readonly before: E; readonly after: E }> = [];
  for (const edge of [...gone].sort(byId)) {
    const next = waiting.get(outletOf(edge))?.shift();
    if (next) pairs.push({ before: edge, after: next });
  }
  return pairs;
}

const editedIn = (diff: WorkflowContentDiff) => [...diff.added, ...diff.removed, ...diff.changed].map((step) => step.id);

/** The steps whose outgoing links changed: a link is the step it leaves. */
const relinkedIn = (diff: WorkflowContentDiff) =>
  [...diff.links.removed, ...diff.links.added, ...diff.links.rerouted.map((pair) => pair.after)].map((link) => link.source);

/** Steps whose content or links changed; a link is the step it leaves. */
export function changedSteps<G extends WorkflowGraphShape>(diff: WorkflowContentDiff<G>): ReadonlySet<string> {
  return new Set([...editedIn(diff), ...relinkedIn(diff)]);
}

/**
 * One step as a run of changes left it: as it was before the first of them
 * and after the last, null where it did not exist, and where its outgoing
 * links led then and now. A move alone is no change.
 */
export interface StepEdit<S extends WorkflowStepLike = WorkflowStepLike> {
  readonly before: S | null;
  readonly after: S | null;
  readonly links: { readonly before: string; readonly after: string };
}

/** A run's edits by step id, in the order each step first changed. */
export type StepEdits<S extends WorkflowStepLike = WorkflowStepLike> = ReadonlyMap<string, StepEdit<S>>;

const outgoing = (graph: WorkflowGraphShape, id: string) =>
  graph.edges
    .filter((edge) => edge.source === id)
    .map((edge) => `${edge.sourceHandle ?? ""}>${edge.target}`)
    .sort()
    .join(" ");

/** `edits` with one more change of the run, from `before` to `after`, folded in; `edits` itself when it changed no step. */
export function foldStepEdits<G extends WorkflowGraphShape>(
  edits: StepEdits<StepOf<G>>,
  before: WorkflowContent<G>,
  after: WorkflowContent<G>,
): StepEdits<StepOf<G>> {
  const diff = diffWorkflowContent(before, after);
  const ids = new Set([...editedIn(diff), ...relinkedIn(diff)]);
  if (ids.size === 0) return edits;
  const was = new Map(before.graph.nodes.map((node) => [node.id, node]));
  const now = new Map(after.graph.nodes.map((node) => [node.id, node]));
  const next = new Map(edits);
  for (const id of ids) {
    const known = edits.get(id);
    next.set(id, {
      before: known ? known.before : (was.get(id) ?? null),
      after: now.get(id) ?? null,
      links: { before: known ? known.links.before : outgoing(before.graph, id), after: outgoing(after.graph, id) },
    });
  }
  return next;
}

export type StepChangeKind = "added" | "edited" | "removed";

/** A step a run changed, as a person counts it. `fields` name an edited step's changed fields; none for a step whose links alone changed. */
export interface StepChange<S extends WorkflowStepLike = WorkflowStepLike> {
  readonly id: string;
  readonly kind: StepChangeKind;
  readonly before: S | null;
  readonly after: S | null;
  readonly fields: ReadonlyArray<FieldChange>;
}

/** What changed between two versions of one step: its kind, label and config, field by field. */
export function stepFieldChanges<S extends WorkflowStepLike>(before: S, after: S): ReadonlyArray<FieldChange> {
  const alone = (node: S) => ({ graph: { nodes: [node], edges: [] }, name: "", description: null });
  return diffWorkflowContent(alone(before), alone(after)).changed[0]?.fields ?? [];
}

/**
 * The steps a run of changes changed, as a person counts them, first
 * changed first: each it added, removed or edited (kind, label or config).
 * One that only gained or lost a link is not one, unless the run changed
 * links and nothing else; then the steps those links leave count, so a
 * rerouting still reads as a change. A step added and removed again, or
 * changed and changed back, is not one.
 */
export function stepChangesOf<S extends WorkflowStepLike>(edits: StepEdits<S>): ReadonlyArray<StepChange<S>> {
  const changes: Array<StepChange<S>> = [];
  for (const [id, { before, after }] of edits) {
    if (before === null && after === null) continue;
    if (before === null || after === null) {
      changes.push({ id, kind: before === null ? "added" : "removed", before, after, fields: [] });
      continue;
    }
    const fields = stepFieldChanges(before, after);
    if (fields.length > 0) changes.push({ id, kind: "edited", before, after, fields });
  }
  if (changes.length > 0) return changes;
  return [...edits].flatMap(([id, { before, after, links }]) =>
    links.before !== links.after && before !== null && after !== null ? [{ id, kind: "edited" as const, before, after, fields: [] }] : [],
  );
}

/** Every step a change from `before` to `after` touched, a move included. */
export function touchedSteps<G extends WorkflowGraphShape>(before: WorkflowContent<G>, after: WorkflowContent<G>): ReadonlyArray<string> {
  const diff = diffWorkflowContent(before, after);
  return [...new Set([...changedSteps(diff), ...diff.moved.map((move) => move.id)])];
}

/** One run of `after`'s text, marked changed unless it is part of the longest run of words shared with `before`. */
export interface WordRun {
  readonly text: string;
  readonly changed: boolean;
}

/** Beyond this many pairs of words (whitespace not counted) the diff gives up and marks the whole text. */
export const MAX_WORD_PAIRS = 250_000;

const tokensOf = (text: string) => text.split(/(\s+)/u).filter((token) => token.length > 0);

const isWord = (token: string) => /\S/u.test(token);

/**
 * `after` as runs of words, each marked changed unless it is part of the
 * longest common subsequence of words with `before`. Whitespace takes no
 * part in the match and is never marked.
 */
export function wordDiff(before: string, after: string): ReadonlyArray<WordRun> {
  const tokens = tokensOf(after);
  const next = tokens.filter(isWord);
  const prev = tokensOf(before).filter(isWord);
  if (prev.length * next.length > MAX_WORD_PAIRS) return after ? [{ text: after, changed: true }] : [];
  const width = prev.length + 1;
  const suffixLcs = new Uint32Array((next.length + 1) * width);
  for (let i = next.length - 1; i >= 0; i--) {
    for (let j = prev.length - 1; j >= 0; j--) {
      suffixLcs[i * width + j] =
        next[i] === prev[j]
          ? (suffixLcs[(i + 1) * width + j + 1] as number) + 1
          : Math.max(suffixLcs[(i + 1) * width + j] as number, suffixLcs[i * width + j + 1] as number);
    }
  }
  const kept = new Uint8Array(next.length);
  let i = 0;
  let j = 0;
  while (i < next.length && j < prev.length) {
    if (next[i] === prev[j]) {
      kept[i] = 1;
      i += 1;
      j += 1;
    } else if ((suffixLcs[i * width + j + 1] as number) >= (suffixLcs[(i + 1) * width + j] as number)) {
      j += 1;
    } else {
      i += 1;
    }
  }
  const runs: Array<{ text: string; changed: boolean }> = [];
  const push = (text: string, changed: boolean) => {
    const last = runs.at(-1);
    if (last && last.changed === changed) last.text += text;
    else runs.push({ text, changed });
  };
  let word = 0;
  for (const token of tokens) {
    if (!isWord(token)) {
      push(token, false);
      continue;
    }
    push(token, kept[word] === 0);
    word += 1;
  }
  return runs;
}

/** Whole steps and links set or removed, plus the scalars that changed. */
export interface WorkflowContentPatch<G extends WorkflowGraphShape = WorkflowGraphShape> {
  readonly nodes: { readonly set: ReadonlyArray<StepOf<G>>; readonly remove: ReadonlyArray<string> };
  readonly edges: { readonly set: ReadonlyArray<LinkOf<G>>; readonly remove: ReadonlyArray<string> };
  /** Null when the layout was removed. */
  readonly layout?: NonNullable<G["layout"]> | null;
  readonly name?: string;
  readonly description?: string | null;
}

/** How one version reaches a chart that holds its parent: a patch, or the whole content when a patch cannot say it. */
export type WorkflowContentUpdate<G extends WorkflowGraphShape = WorkflowGraphShape> =
  | { readonly kind: "patch"; readonly patch: WorkflowContentPatch<G> }
  | { readonly kind: "content"; readonly content: WorkflowContent<G> };

/**
 * The update that turns `before` into `after`: a patch when applying it
 * reproduces `after` exactly, order and key order included, and otherwise
 * the whole content (steps were reordered, say).
 */
export function makeContentPatch<G extends WorkflowGraphShape>(
  before: WorkflowContent<G>,
  after: WorkflowContent<G>,
): WorkflowContentUpdate<G> {
  const from = facetsOf(before);
  const to = facetsOf(after);
  const patch: WorkflowContentPatch<G> = {
    nodes: {
      set: after.graph.nodes.filter((node) => from.stepSignature.get(node.id) !== to.stepSignature.get(node.id)),
      remove: before.graph.nodes.filter((node) => !to.nodes.has(node.id)).map((node) => node.id),
    },
    edges: {
      set: after.graph.edges.filter((edge) => linkValue(from, edge.id) !== linkValue(to, edge.id)),
      remove: before.graph.edges.filter((edge) => linkValue(to, edge.id) === undefined).map((edge) => edge.id),
    },
    ...(before.graph.layout === after.graph.layout ? {} : { layout: (after.graph.layout ?? null) as NonNullable<G["layout"]> | null }),
    ...(before.name === after.name ? {} : { name: after.name }),
    ...(before.description === after.description ? {} : { description: after.description }),
  };
  return JSON.stringify(applyPatch(before, patch)) === JSON.stringify(after) ? { kind: "patch", patch } : { kind: "content", content: after };
}

/** `before` with `update` applied: a set step or link replaces its namesake in place or is appended, in patch order. */
export function applyContentPatch<G extends WorkflowGraphShape>(
  before: WorkflowContent<G>,
  update: WorkflowContentUpdate<G>,
): WorkflowContent<G> {
  return update.kind === "content" ? update.content : applyPatch(before, update.patch);
}

function applyPatch<G extends WorkflowGraphShape>(before: WorkflowContent<G>, patch: WorkflowContentPatch<G>): WorkflowContent<G> {
  const apply = <T extends { readonly id: string }>(items: ReadonlyArray<T>, change: { readonly set: ReadonlyArray<T>; readonly remove: ReadonlyArray<string> }) => {
    const set = new Map(change.set.map((item) => [item.id, item]));
    const remove = new Set(change.remove);
    const kept = items.filter((item) => !remove.has(item.id)).map((item) => set.get(item.id) ?? item);
    const present = new Set(kept.map((item) => item.id));
    return [...kept, ...change.set.filter((item) => !present.has(item.id))];
  };
  const { layout: _layout, ...graphRest } = before.graph;
  const layout = patch.layout === undefined ? before.graph.layout : (patch.layout ?? undefined);
  const graph = {
    ...graphRest,
    nodes: apply(before.graph.nodes, patch.nodes),
    edges: apply(before.graph.edges, patch.edges),
    ...(layout === undefined ? {} : { layout }),
  } as unknown as G;
  return {
    ...before,
    graph,
    name: patch.name ?? before.name,
    description: patch.description === undefined ? before.description : patch.description,
  };
}

/** The graph caps, `WORKFLOW_LIMITS` from the contracts. */
export interface GraphLimits {
  readonly nodes: number;
  readonly edges: number;
}

/** The facet a note names. `step` is the step's existence and kind. */
export type FacetField = "step" | "label" | `config.${string}` | "link" | "name" | "description";

/** A facet a merge conflict or a refused undo names. */
export interface FacetNote {
  /** The step it is about; a link's is the step it leaves. Null for the workflow's name or description. */
  readonly stepId: string | null;
  /**
   * The step's label in the merge or `head` when that has the step, else in
   * the newest version that does, ours before theirs before base for a
   * merge. Null for the workflow itself.
   */
  readonly stepLabel: string | null;
  /** For a link, the step it leads to. */
  readonly targetId?: string;
  readonly field: FacetField;
}

/**
 * A change of theirs the merge does not hold. Positions, the layout and the
 * order never conflict. A change to a step is named `step` when the merge
 * and theirs disagree on whether it exists or its kind, else by its field.
 * A link is named by its ends in theirs or, when theirs removed it, in the
 * merge. Conflicts come in the merge's step order, the workflow itself first.
 */
export type MergeConflict = FacetNote;

export type MergeResult<G extends WorkflowGraphShape = WorkflowGraphShape> =
  | { readonly kind: "merged"; readonly content: WorkflowContent<G>; readonly conflicts: ReadonlyArray<MergeConflict> }
  /** The merge would hold more steps or links than `limits` allows. The caller keeps ours whole. */
  | { readonly kind: "over-limit" };

/**
 * Three-way merge: `theirs`' changes since `base` carried onto `ours`.
 *
 * - Where both changed one facet, ours wins.
 * - The two overlap on a step when they disagree on its kind, label, config
 *   or links, and either one changed whether it exists or its kind while
 *   the other changed its kind, label, config or a link to or from it, or
 *   both changed links to or from it. A link removed with one of its steps
 *   is not a change at its other end. Moving a step is not a change.
 * - For every step they overlap on, the result takes ours' step, and ours'
 *   side of every link that, in any of the three, touches it or a step one
 *   of ours' links joins it to. Such a neighbour stays when theirs removed
 *   it. Nothing reaches further.
 * - Every other change of theirs merges, except that a step theirs added
 *   and linked into goes when the result has no link into it, and a link
 *   theirs rerouted stays when its new end did not.
 * - Each change of theirs the result does not hold is a {@link MergeConflict}.
 * - Order is cosmetic like position: ours when ours reordered, else theirs,
 *   with steps the chosen order lacks at their index in the other side.
 *
 * - When that result would put a step on a loop no side has, or leave a
 *   step unreachable that ours reaches and theirs reaches or does not have, for example because each side cut a different route to one
 *   step, the result is ours whole and every change of theirs is a conflict.
 *
 * Laws: merge(b, o, b) = o, merge(b, b, t) = t, merge(b, x, x) = x, and a
 * merge never puts a step on a loop that no side has on one, or strands a
 * step ours reaches that theirs reaches or does not have.
 */
export function mergeWorkflowContent<G extends WorkflowGraphShape>(input: {
  readonly base: WorkflowContent<G>;
  readonly ours: WorkflowContent<G>;
  readonly theirs: WorkflowContent<G>;
  readonly limits: GraphLimits;
}): MergeResult<G> {
  const base = facetsOf(input.base);
  const ours = facetsOf(input.ours);
  const theirs = facetsOf(input.theirs);
  const oursHolds = overlapOf(base, ours, theirs);
  let resolved = new Map<string, Facet>();
  for (const text of unionKeys(ours, theirs, base)) {
    const mine = ours.byKey.get(text);
    const pick = oursHolds(text) || valueOf(mine) !== valueOf(base.byKey.get(text)) ? mine : theirs.byKey.get(text);
    if (pick) resolved.set(text, pick);
  }
  settleSteps(resolved, ours);
  dropUnlinkedNewSteps(resolved, base, ours, theirs);
  keepReplacedLinks(resolved, base, ours, theirs);
  let content = contentOf(resolved, input.ours, [ours, theirs, base]);
  if (breaksRuns(input.ours.graph, input.theirs.graph, content.graph)) {
    resolved = new Map(ours.byKey);
    content = contentOf(resolved, input.ours, [ours, theirs, base]);
  }
  if (content.graph.nodes.length > input.limits.nodes || content.graph.edges.length > input.limits.edges) return { kind: "over-limit" };
  const index = new Map(content.graph.nodes.map((node, at) => [node.id, at]));
  const held = (text: string) => {
    const facet = resolved.get(text);
    if (facet?.key.kind !== "link") return facet;
    const edge = facet.raw as WorkflowLinkLike;
    return index.has(edge.source) && index.has(edge.target) ? facet : undefined;
  };
  const labelOf = labelIn([{ nodes: new Map(content.graph.nodes.map((node) => [node.id, node])) }, ours, theirs, base]);
  const conflicts: MergeConflict[] = [];
  for (const text of unionKeys(theirs, base)) {
    const value = valueOf(theirs.byKey.get(text));
    if (value === valueOf(base.byKey.get(text)) || value === valueOf(held(text))) continue;
    const { key } = facetIn(text, theirs, base);
    if (isCosmetic(key)) continue;
    const wholeStep = isStepKey(key) && kindOf(theirs.byKey, key.id) !== kindOf(resolved, key.id);
    conflicts.push(wholeStep ? { stepId: key.id, stepLabel: labelOf(key.id), field: "step" } : noteOf(text, labelOf, [theirs, ours, base]));
  }
  return { kind: "merged", content, conflicts: sortedUniqueByStep(conflicts, index) };
}

/**
 * Whether `after` puts a step on a loop no side has one on, or leaves a step
 * unreachable that ours reaches and theirs reaches or does not have. A step
 * theirs cut off, or one only theirs has under a cut of ours, is theirs' or
 * ours' own doing and does not count.
 */
function breaksRuns(ours: WorkflowGraphShape, theirs: WorkflowGraphShape, after: WorkflowGraphShape): boolean {
  const issuesOf = (graph: WorkflowGraphShape) => {
    const looped = new Set<string>();
    const stranded = new Set<string>();
    for (const issue of validateWorkflowGraph(graph)) {
      if (!issue.nodeId) continue;
      if (issue.code === "cycle") looped.add(issue.nodeId);
      else if (issue.code === "unreachable") stranded.add(issue.nodeId);
    }
    return { looped, stranded, has: new Set(graph.nodes.map((node) => node.id)) };
  };
  const [mine, yours, now] = [issuesOf(ours), issuesOf(theirs), issuesOf(after)];
  for (const id of now.looped) if (!mine.looped.has(id) && !yours.looped.has(id)) return true;
  const reaches = (side: ReturnType<typeof issuesOf>, id: string) => side.has.has(id) && !side.stranded.has(id);
  for (const id of now.stranded) if (reaches(mine, id) && (reaches(yours, id) || !yours.has.has(id))) return true;
  return false;
}

type LinksAt = ReadonlyMap<string, ReadonlyArray<Facet>>;

const kindOf = (facets: ReadonlyMap<string, Facet>, id: string) => valueOf(facets.get(keyString({ kind: "step", id })));

function overlapOf(base: Facets, ours: Facets, theirs: Facets): (text: string) => boolean {
  const oursAt = linksAt(ours);
  const theirsAt = linksAt(theirs);
  const oursLinked = relinked(base, ours);
  const theirsLinked = relinked(base, theirs);
  const signature = (at: LinksAt, id: string) => (at.get(id) ?? []).map((facet) => facet.value).sort().join("\u0001");
  const differs = (id: string) => ours.stepContent.get(id) !== theirs.stepContent.get(id) || signature(oursAt, id) !== signature(theirsAt, id);
  const edited = (side: Facets, linked: ReadonlySet<string>, id: string) => side.stepContent.get(id) !== base.stepContent.get(id) || linked.has(id);
  const involved = new Set<string>();
  for (const id of new Set([...base.nodes.keys(), ...ours.nodes.keys(), ...theirs.nodes.keys()])) {
    const removedAndEdited =
      (kindOf(ours.byKey, id) !== kindOf(base.byKey, id) && edited(theirs, theirsLinked, id)) ||
      (kindOf(theirs.byKey, id) !== kindOf(base.byKey, id) && edited(ours, oursLinked, id));
    if ((removedAndEdited || (oursLinked.has(id) && theirsLinked.has(id))) && differs(id)) involved.add(id);
  }
  const held = new Set(involved);
  for (const id of involved) for (const end of endsAt(oursAt, id)) held.add(end);
  return (text) => {
    for (const side of [ours, theirs, base]) {
      const facet = side.byKey.get(text);
      if (!facet) continue;
      if (isStepKey(facet.key)) return involved.has(facet.key.id) || (facet.key.kind === "step" && held.has(facet.key.id) && !theirs.nodes.has(facet.key.id));
      if (facet.key.kind !== "link") return false;
      const edge = facet.raw as WorkflowLinkLike;
      if (held.has(edge.source) || held.has(edge.target)) return true;
    }
    return false;
  };
}

const stepsIn = (resolved: ReadonlyMap<string, Facet>): ReadonlySet<string> =>
  new Set([...resolved.values()].flatMap((facet) => (facet.key.kind === "step" ? [facet.key.id] : [])));

function linksIn(resolved: ReadonlyMap<string, Facet>): ReadonlyArray<WorkflowLinkLike> {
  const steps = stepsIn(resolved);
  const links: WorkflowLinkLike[] = [];
  for (const facet of resolved.values()) {
    if (facet.key.kind !== "link") continue;
    const edge = facet.raw as WorkflowLinkLike;
    if (steps.has(edge.source) && steps.has(edge.target)) links.push(edge);
  }
  return links;
}

function dropUnlinkedNewSteps(resolved: Map<string, Facet>, base: Facets, ours: Facets, theirs: Facets): void {
  const added = [...theirs.nodes.keys()].filter((id) => !base.nodes.has(id) && !ours.nodes.has(id));
  if (added.length === 0) return;
  const linkedInTheirs = new Set(linksIn(theirs.byKey).map((edge) => edge.target));
  const fresh = added.filter((id) => linkedInTheirs.has(id));
  for (let dropped = true; dropped; ) {
    const linkedNow = new Set(linksIn(resolved).map((edge) => edge.target));
    const steps = stepsIn(resolved);
    const unlinked = fresh.filter((id) => steps.has(id) && !linkedNow.has(id));
    for (const id of unlinked) for (const text of theirs.stepKeys.get(id) ?? []) resolved.delete(text);
    dropped = unlinked.length > 0;
  }
}

function keepReplacedLinks(resolved: Map<string, Facet>, base: Facets, ours: Facets, theirs: Facets): void {
  const linkIn = (side: Facets, edge: WorkflowLinkLike) => side.byKey.get(keyString({ kind: "link", id: edge.id }));
  const gone = linksIn(base.byKey).filter((edge) => !linkIn(theirs, edge) && valueOf(linkIn(ours, edge)) === valueOf(linkIn(base, edge)));
  if (gone.length === 0) return;
  const added = linksIn(theirs.byKey).filter((edge) => !linkIn(base, edge));
  const kept = new Set(linksIn(resolved).map((edge) => edge.id));
  for (const { before, after } of pairReroutes(gone, added)) if (!kept.has(after.id)) resolved.set(keyString({ kind: "link", id: before.id }), linkIn(base, before) as Facet);
}

function linksAt(side: Facets): LinksAt {
  const at = new Map<string, Facet[]>();
  for (const facet of side.byKey.values()) {
    if (facet.key.kind !== "link") continue;
    const edge = facet.raw as WorkflowLinkLike;
    for (const id of [edge.source, edge.target]) {
      const list = at.get(id);
      if (list) list.push(facet);
      else at.set(id, [facet]);
    }
  }
  return at;
}

function* endsAt(at: LinksAt, id: string): Iterable<string> {
  for (const facet of at.get(id) ?? []) {
    const edge = facet.raw as WorkflowLinkLike;
    yield edge.source === id ? edge.target : edge.source;
  }
}

function relinked(base: Facets, side: Facets): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const text of unionKeys(base, side)) {
    const was = base.byKey.get(text);
    const now = side.byKey.get(text);
    if ((now ?? was)?.key.kind !== "link" || valueOf(was) === valueOf(now)) continue;
    const gone = was?.raw as WorkflowLinkLike | undefined;
    if (!now && gone && (!side.nodes.has(gone.source) || !side.nodes.has(gone.target))) continue;
    for (const facet of [was, now]) {
      if (!facet) continue;
      const edge = facet.raw as WorkflowLinkLike;
      ids.add(edge.source);
      ids.add(edge.target);
    }
  }
  return ids;
}

/**
 * One saved change in a run of consecutive versions, flagged `undoing` when
 * it belongs to what is being taken back (an agent request's group, or one
 * local edit), and not when it is someone else's that came in between or after.
 */
export interface ContentChange<G extends WorkflowGraphShape = WorkflowGraphShape> {
  readonly before: WorkflowContent<G>;
  readonly after: WorkflowContent<G>;
  readonly undoing: boolean;
}

/**
 * A facet the undone changes and someone else after the first of them both
 * touched; a facet someone else changed on a step the undone changes made;
 * a step the undone changes made, or one re-kinded between kinds with
 * different outlets, named `step` when the other side changed a link at it;
 * or a step someone else removed that the undo would link back to, named
 * `step`. A link is named by its ends in `head`, or the last ends it had.
 * Refusals come in `head`'s step order, the workflow itself first.
 */
export type RevertRefusal = FacetNote;

export type RevertResult<G extends WorkflowGraphShape = WorkflowGraphShape> =
  | {
      readonly kind: "reverted";
      /**
       * `head` with every facet the undone changes touched back as it was
       * before the first of them, except positions, the layout and the order
       * someone else changed since, which stay as `head` has them. A step the
       * undo brings back that someone else moved comes back where they put it.
       */
      readonly content: WorkflowContent<G>;
      /** How many facets differ from `head`, positions, the layout and the order included. */
      readonly reverted: number;
    }
  /** Nothing was undone. Never empty. */
  | { readonly kind: "refused"; readonly refused: ReadonlyArray<RevertRefusal> }
  /** Putting back what the undone changes removed would hold more steps or links than `limits` allows. */
  | { readonly kind: "over-limit" };

/**
 * Undo, all or nothing. `changes` runs in order from the first undone change
 * up to `head`. Whatever happened between one change's `after` and the next
 * one's `before`, or after the last change, counts as someone else's.
 *
 * The facets that can refuse an undo are a step's existence and kind, its
 * label, each config key, each link, the name and the description. The undo
 * refuses when, after the first undone change, someone else:
 *
 * - touched a facet the undone changes touched;
 * - touched any facet of a step the undone changes made, or a link at it;
 * - removed a step the undo would link back to;
 * - changed a link at a step the undone changes re-kinded, or re-kinded a
 *   step the undone changes linked at, when the two kinds have different
 *   outlets.
 *
 * Nothing else refuses: two links changed at one step, a link the undone
 * changes drew to a step someone else made, or a link someone else removed
 * at a step the undone changes then removed. A refusal changes nothing and
 * names every facet that refused. Otherwise every facet the undone changes
 * touched goes back to its value before the first of them, and someone
 * else's changes to other facets stay.
 *
 * Positions, the layout and the order never refuse an undo. They follow it
 * where nobody else changed them, and stay as `head` has them otherwise.
 *
 * Redo is the revert of the Undo's own change. Undoing a change of a
 * step's kind keeps config values someone else set on the step since,
 * which the old kind may not use.
 */
export function revertChanges<G extends WorkflowGraphShape>(input: {
  readonly changes: ReadonlyArray<ContentChange<G>>;
  readonly head: WorkflowContent<G>;
  readonly limits: GraphLimits;
}): RevertResult<G> {
  const { changes, head, limits } = input;
  const first = changes[0];
  if (!first) return { kind: "reverted", content: head, reverted: 0 };
  const span = changes.flatMap((change, at) => {
    const next = changes[at + 1];
    return [change, { before: change.after, after: next?.before ?? head, undoing: false }];
  });
  const seen = new Map<WorkflowContent<G>, Facets<G>>();
  const facetsOnce = (content: WorkflowContent<G>) => seen.get(content) ?? (seen.set(content, facetsOf(content)).get(content) as Facets<G>);
  const undone = new Set<string>();
  const foreign = new Set<string>();
  const foreignLast = new Map<string, Facet | undefined>();
  const rekinded = { undone: new Set<string>(), foreign: new Set<string>() };
  for (const change of span) {
    const step = { before: facetsOnce(change.before), after: facetsOnce(change.after) };
    for (const text of unionKeys(step.before, step.after)) {
      if (!touches(step, text)) continue;
      (change.undoing ? undone : foreign).add(text);
      if (!change.undoing) foreignLast.set(text, step.after.byKey.get(text));
    }
    for (const id of step.before.nodes.keys()) {
      const kind = { before: step.before.nodes.get(id)?.type, after: step.after.nodes.get(id)?.type };
      if (kind.after !== undefined && outletsOf(kind.before) !== outletsOf(kind.after)) (change.undoing ? rekinded.undone : rekinded.foreign).add(id);
    }
  }
  const now = facetsOnce(head);
  const was = facetsOnce(first.before);
  const latestFirst = [now, ...[...seen.values()].reverse()];
  const stepText = (id: string) => keyString({ kind: "step", id });
  const made = new Set<string>();
  for (const text of undone) {
    const { key } = facetIn(text, ...latestFirst);
    if (key.kind === "step" && !was.nodes.has(key.id)) made.add(key.id);
  }
  const overlap = new Set<string>();
  for (const text of foreign) {
    const { key } = facetIn(text, ...latestFirst);
    if (isCosmetic(key)) continue;
    if (undone.has(text) || (isStepKey(key) && made.has(key.id))) overlap.add(text);
  }
  for (const id of linkedSteps(foreign, latestFirst)) if (made.has(id) || rekinded.undone.has(id)) overlap.add(stepText(id));
  for (const id of linkedSteps(undone, latestFirst)) if (rekinded.foreign.has(id)) overlap.add(stepText(id));
  for (const id of linkedSteps(undone, [was])) if (!undone.has(stepText(id)) && !now.nodes.has(id)) overlap.add(stepText(id));
  if (overlap.size > 0) {
    const labelOf = labelIn(latestFirst);
    const index = new Map(head.graph.nodes.map((node, at) => [node.id, at]));
    return { kind: "refused", refused: sortedUniqueByStep([...overlap].map((text) => noteOf(text, labelOf, latestFirst)), index) };
  }
  const resolved = new Map(now.byKey);
  for (const text of undone) {
    if (foreign.has(text)) {
      const moved = foreignLast.get(text) ?? was.byKey.get(text);
      if (!now.byKey.has(text) && moved?.key.kind === "position") resolved.set(text, moved);
      continue;
    }
    const facet = was.byKey.get(text);
    if (facet) resolved.set(text, facet);
    else resolved.delete(text);
  }
  settleSteps(resolved, now);
  const content = contentOf(resolved, head, [now, was, ...latestFirst]);
  if (content.graph.nodes.length > limits.nodes || content.graph.edges.length > limits.edges) return { kind: "over-limit" };
  const after = facetsOf(content);
  return { kind: "reverted", content, reverted: unionKeys(now, after).filter((text) => touches({ before: now, after }, text)).length };
}

function linkedSteps(texts: Iterable<string>, sides: ReadonlyArray<Facets>): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const text of texts) {
    for (const side of sides) {
      const facet = side.byKey.get(text);
      if (facet?.key.kind !== "link") continue;
      const edge = facet.raw as WorkflowLinkLike;
      ids.add(edge.source);
      ids.add(edge.target);
    }
  }
  return ids;
}

const labelIn =
  (sides: ReadonlyArray<{ readonly nodes: ReadonlyMap<string, WorkflowStepLike> }>) =>
  (id: string): string | null => {
    for (const side of sides) {
      const label = side.nodes.get(id)?.label;
      if (label !== undefined) return label;
    }
    return null;
  };

function noteOf(text: string, labelOf: (id: string) => string | null, sides: ReadonlyArray<Facets>): FacetNote {
  const { key, raw } = facetIn(text, ...sides);
  if (key.kind === "link") {
    const edge = raw as WorkflowLinkLike;
    return { stepId: edge.source, stepLabel: labelOf(edge.source), targetId: edge.target, field: "link" };
  }
  if (isStepKey(key)) {
    const field = key.kind === "config" ? (`config.${key.key}` as const) : key.kind === "label" ? "label" : "step";
    return { stepId: key.id, stepLabel: labelOf(key.id), field };
  }
  return { stepId: null, stepLabel: null, field: key.kind === "name" ? "name" : "description" };
}

const outletsOf = (kind: string | undefined): string => (kind !== undefined && workflowStepBranches(kind) ? CONDITION_OUTLETS.join() : "");

const outletOf = (edge: WorkflowLinkLike): string => `${edge.source}\u0000${edge.sourceHandle ?? ""}`;

const linkValue = (side: Facets, id: string) => valueOf(side.byKey.get(keyString({ kind: "link", id })));

const touches = (change: { readonly before: Facets; readonly after: Facets }, text: string): boolean =>
  valueOf(change.before.byKey.get(text)) !== valueOf(change.after.byKey.get(text));

function stepField(key: FacetKey): StepField | null {
  switch (key.kind) {
    case "step":
      return "type";
    case "label":
      return "label";
    case "config":
      return `config.${key.key}`;
    default:
      return null;
  }
}

const compareText = (left: string, right: string): number => (left < right ? -1 : left > right ? 1 : 0);

const fieldRank = (field: string): string => (field === "step" ? "0" : field === "type" ? "1" : field === "label" ? "2" : `3${field}`);

function sortedUniqueByStep<T extends { readonly stepId: string | null; readonly targetId?: string; readonly field: string }>(
  items: ReadonlyArray<T>,
  index: ReadonlyMap<string, number>,
): ReadonlyArray<T> {
  const place = (stepId: string | null) => (stepId === null ? -1 : (index.get(stepId) ?? Number.MAX_SAFE_INTEGER));
  const unique = new Map(items.map((item) => [`${item.stepId ?? ""}\u0000${item.field}\u0000${item.targetId ?? ""}`, item]));
  return [...unique.values()].sort(
    (left, right) =>
      place(left.stepId) - place(right.stepId) ||
      compareText(left.stepId ?? "", right.stepId ?? "") ||
      compareText(fieldRank(left.field), fieldRank(right.field)) ||
      compareText(left.targetId ?? "", right.targetId ?? ""),
  );
}
